import React, { useState, useEffect } from "react";
import { Card, Tag, Typography, Row, Col, Statistic, notification, Input, Select, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { loadSummaries, loadAnswers, getUserSettings, saveUserSettings } from "../store/app/app.slice";
import { FileTextOutlined, MessageOutlined, EditOutlined } from "@ant-design/icons";
import moment from "moment";
import SummaryOrderView from "../components/dialogs/SummaryOrderView";
import CreateSummaryDialog from "../components/dialogs/CreateSummaryDialog";
import CreateAnswerDialog from "../components/dialogs/CreateAnswerDialog";
import { getUserProfile } from "../utils/authHelper";

const { TextArea } = Input;

const { Title } = Typography;


export default function SummariesAnswers({ viewMode = "both" }) {
  const dispatch = useDispatch();
  const { summaries, answers, isSummariesLoading, isAnswersLoading } = useSelector((state) => state.app);
  const [summaryTitleContent, setSummaryTitleContent] = useState(null);
  const [userSettingsSummaryOrder, setUserSettingsSummaryOrder] = useState({});
  const [selectedSummaryFilter, setSelectedSummaryFilter] = useState(null);
  const [summarySearchTerm, setSummarySearchTerm] = useState('');
  
  // Dialog states for editing
  const [showEditSummaryDialog, setShowEditSummaryDialog] = useState(false);
  const [showEditAnswerDialog, setShowEditAnswerDialog] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  
  // Get current user profile
  const userProfile = getUserProfile();
  const currentUserId = userProfile?.sub;
  
  // Filter summaries and answers for current user (created by or linked to user)
  // This follows the exact same logic used in StackDetailTable and StackLetter components
  const userSummaries = summaries.filter(summary => 
    summary.createdBy === currentUserId || 
    (summary.userIds && Array.from(summary.userIds).includes(currentUserId))
  );

  // Filter summaries based on search term
  const filteredSummaries = userSummaries.filter(summary => {
    if (!summarySearchTerm) return true;
    const searchLower = summarySearchTerm.toLowerCase();
    return (
      (summary.title && summary.title.toLowerCase().includes(searchLower)) ||
      (summary.titleUrdu && summary.titleUrdu.toLowerCase().includes(searchLower)) ||
      (summary.content && summary.content.toLowerCase().includes(searchLower)) ||
      (summary.contentUrdu && summary.contentUrdu.toLowerCase().includes(searchLower))
    );
  });
  
  const userAnswers = answers.filter(answer => 
    answer.createdBy === currentUserId || 
    (answer.userIds && Array.from(answer.userIds).includes(currentUserId))
  );
  
  // Debug logging to verify filtering

  // Handle editing summaries
  const handleEditSummary = (summary) => {
    setSelectedSummary(summary);
    setShowEditSummaryDialog(true);
  };

  // Handle editing answers
  const handleEditAnswer = (answer) => {
    setSelectedAnswer(answer);
    setShowEditAnswerDialog(true);
  };
  


  useEffect(() => {
    //dispatch(loadSummaries({}));
    //dispatch(loadAnswers({}));
    
    // Load user settings for summary order
    dispatch(getUserSettings({
      finalData: { settingName: "SUMMARY_ORDER" },
      callbackSuccess: (response) => {
        if (response && response.settingData) {
          setUserSettingsSummaryOrder(response.settingData);
        }
      },
      callbackError: (error) => {
        console.error("Error loading user settings:", error);
      }
    }));
  }, []);





  const summaryColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <strong>{text || 'Untitled Summary'}</strong>,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (text) => text ? `${text.substring(0, 100)}${text.length > 100 ? '...' : ''}` : 'No content',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'orange' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'In Progress', value: 'in_progress' },
        { text: 'Draft', value: 'draft' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => moment(a.created).unix() - moment(b.created).unix(),
    },
    {
      title: 'Author',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (user) => user?.name || 'Unknown',
    },
  ];

  const answerColumns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (text) => <strong>{text || 'Untitled Answer'}</strong>,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      render: (text) => text ? `${text.substring(0, 100)}${text.length > 100 ? '...' : ''}` : 'No content',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'completed' ? 'green' : status === 'in_progress' ? 'orange' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
      filters: [
        { text: 'Completed', value: 'completed' },
        { text: 'In Progress', value: 'in_progress' },
        { text: 'Draft', value: 'draft' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      render: (date) => moment(date).format('YYYY-MM-DD HH:mm'),
      sorter: (a, b) => moment(a.created).unix() - moment(b.created).unix(),
    },
    {
      title: 'Author',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (user) => user?.name || 'Unknown',
    },
  ];



  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col span={24}>
          <Title level={2}>
            {viewMode === "summaries" && <FileTextOutlined style={{ marginRight: '8px' }} />}
            {viewMode === "answers" && <MessageOutlined style={{ marginRight: '8px' }} />}
            {viewMode === "both" && <FileTextOutlined style={{ marginRight: '8px' }} />}
            {viewMode === "summaries" && "Summaries Management"}
            {viewMode === "answers" && "Answers Management"}
            {viewMode === "both" && "Summaries & Answers Management"}
          </Title>
        </Col>
      </Row>





      {/* Content based on viewMode */}
      {viewMode === "summaries" && (
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Summaries ({summarySearchTerm ? `${filteredSummaries.length} of ${userSummaries.length}` : userSummaries.length})
                  </span>
                  <div style={{ flex: 1, maxWidth: '50%', margin: '0 16px' }}>
                    <Input
                      placeholder="Search for summaries"
                      allowClear
                      value={summarySearchTerm}
                      onChange={(e) => setSummarySearchTerm(e.target.value)}
                      className="arabic-font arabic-font-edit"
                      style={{ width: '100%', borderRadius: '8px' }}
                      prefix={<span style={{ color: '#8c8c8c' }}>🔍</span>}
                    />
                  </div>
                  {summaryTitleContent}
                </div>
              }
              loading={isSummariesLoading}
            >
              <div style={{ minHeight: '200px' }}>
                {userSummaries.length > 0 ? (
                  <SummaryOrderView
                    embedded={true}
                    userModeOnly={true}
                    summariesList={userSummaries}
                    allUserSummaries={userSummaries}
                    userSettingsSummaryOrder={userSettingsSummaryOrder}
                    currentSummaryOrder={{}}
                    disabled={!!summarySearchTerm}
                    searchTerm={summarySearchTerm}
                    onSummaryUpdated={(updatedSummary) => {
                      // The updateSummary action already updates the specific summary in Redux state
                      // No need to reload all summaries
                    }}
                    onSaveToSettings={(newOrder) => {
                      dispatch(saveUserSettings({
                        finalData: {
                          userSettingsDto: {
                            settingName: "SUMMARY_ORDER",
                            settingData: newOrder
                          }
                        },
                        callbackSuccess: (response) => {
                          if (response && response.settingData) {
                            setUserSettingsSummaryOrder(response.settingData);
                          }
                          // Reload summaries to get the exact order from backend
                          dispatch(loadSummaries({
                            callbackSuccess: (summaries) => {
                              // Summaries reloaded successfully
                            },
                            callbackError: (error) => {
                              console.error('Error reloading summaries:', error);
                            }
                          }));
                          notification.success({
                            message: 'Saved Successfully',
                            description: 'Summary order has been saved to your user settings.',
                          });
                        },
                        callbackError: (error) => {
                          console.error('Error saving to user settings:', error);
                          notification.error({
                            message: 'Save Error',
                            description: 'Failed to save summary order: ' + error,
                          });
                        }
                      }));
                    }}
                    onSaveToStack={(newOrder) => {
                      notification.info({
                        message: 'Save to Stack',
                        description: 'Save to stack functionality not available in this context. Use "Save as Permanent" instead.',
                      });
                    }}
                    onGetTitleContent={setSummaryTitleContent}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    {summarySearchTerm ? 'No summaries found matching your search' : 'No summaries available'}
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {viewMode === "answers" && (
        <Row gutter={[24, 24]}>
          <Col xs={24}>
            <Card 
              title={
                <span>
                  <MessageOutlined style={{ marginRight: '8px' }} />
                  Answers ({userAnswers.length})
                </span>
              }
              loading={isAnswersLoading}
            >
              <div style={{ minHeight: '200px' }}>
                {/* Filter by Summary */}
                <div style={{ marginBottom: '16px' }}>
                  <Select
                    placeholder="Filter by summary"
                    allowClear
                    showSearch
                    size={"large"}
                    style={{ width: '100%' }}
                    popupClassName="arabic-font arabic-font-edit"
                    className="arabic-font arabic-font-edit"
                    onChange={(summaryId) => { setSelectedSummaryFilter(summaryId); }}
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.titleUrdu ?? '').toLowerCase().includes(input.toLowerCase())}
                    fieldNames={{ label: "titleUrdu", value: "id" }}
                    options={userSummaries}
                  />
                </div>
                
                {userAnswers.length > 0 ? (
                  <div>
                    {/* Filter answers based on selected summary */}
                    {(() => {
                      const filteredAnswers = selectedSummaryFilter 
                        ? userAnswers.filter(answer => 
                            answer.summaries && 
                            Array.from(answer.summaries).some(summary => summary.id === selectedSummaryFilter)
                          )
                        : userAnswers;
                      
                      return filteredAnswers.map((answer) => {
                        const isUrduLanguage = answer.answerLanguages && 
                          Array.from(answer.answerLanguages).some(lang => lang === 'urdu' || lang === 'roman_urdu');
                        
                        return (
                          <div
                            key={answer.id}
                            style={{
                              padding: '16px',
                              margin: '8px 0',
                              border: '1px solid #e8e8e8',
                              borderRadius: '8px',
                              backgroundColor: answer.createdBy === currentUserId ? '#ffffff' : '#f5f5f5',
                              opacity: answer.createdBy === currentUserId ? 1 : 0.8,
                              position: 'relative'
                            }}
                          >
                            <TextArea
                              disabled
                              value={answer.answerEnglish || answer.answerUrdu || answer.answerGerman || 'No content available'}
                              className={isUrduLanguage ? "arabic-font" : "roman-font"}
                              style={{
                                color: '#666',
                                fontSize: '14px',
                                marginBottom: '0px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                resize: 'none',
                                overflow: 'hidden',
                                marginTop: answer.createdBy === currentUserId ? '32px' : '0px',
                              }}
                              rows={0}
                              autoSize={{ minRows: 0, maxRows: 8 }}
                            />
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '0px', color: '#333', fontSize: '12px' }}>
                                  Linked Summaries:
                                </div>
                                {answer.summaries && Array.from(answer.summaries).length > 0 ? (
                                  Array.from(answer.summaries).map(summary => (
                                    <div key={summary.id} style={{
                                      padding: '3px 6px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '3px',
                                      marginBottom: '1px',
                                      border: '1px solid #e9ecef',
                                      fontSize: '8px'
                                    }}
                                    className="arabic-font-tiny"
                                    >
                                      {summary.titleEnglish || summary.titleUrdu || 'Untitled Summary'}
                                    </div>
                                  ))
                                ) : (
                                  <span style={{
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    fontSize: '11px'
                                  }}>
                                    No linked summaries
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {answer.createdBy === currentUserId && (
                                  <Button
                                    type="primary"
                                    shape="circle"
                                    size="small"
                                    icon={<EditOutlined />}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      width: '28px',
                                      height: '28px',
                                      zIndex: 1
                                    }}
                                    onClick={() => handleEditAnswer(answer)}
                                  />
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <span style={{ fontSize: '12px', color: '#999' }}>
                                    <span style={{ fontWeight: '500', color: '#666' }}>Created: </span>
                                    {answer.created ? moment(answer.created).format('MMM DD, YYYY') : 'Unknown date'}
                                  </span>
                                  {answer.createdBy !== currentUserId && answer.createdByName && (
                                    <span style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                      <span style={{ fontWeight: '500', color: '#666' }}>By: </span>
                                      {answer.createdByName}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No answers available
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {viewMode === "both" && (
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card 
              title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Summaries ({summarySearchTerm ? `${filteredSummaries.length} of ${userSummaries.length}` : userSummaries.length})
                  </span>
                  <div style={{ flex: 1, maxWidth: '300px', margin: '0 16px' }}>
                    <Input
                      placeholder="Search summaries..."
                      allowClear
                      size="large"
                      value={summarySearchTerm}
                      onChange={(e) => setSummarySearchTerm(e.target.value)}
                      className="arabic-font arabic-font-edit"
                      style={{ width: '100%', borderRadius: '8px' }}
                      prefix={<span style={{ color: '#8c8c8c' }}>🔍</span>}
                    />
                  </div>
                  {summaryTitleContent}
                </div>
              }
              loading={isSummariesLoading}
            >
              <div style={{ minHeight: '200px' }}>
                {userSummaries.length > 0 ? (
                  <SummaryOrderView
                    embedded={true}
                    userModeOnly={true}
                    summariesList={userSummaries}
                    allUserSummaries={userSummaries}
                    userSettingsSummaryOrder={userSettingsSummaryOrder}
                    currentSummaryOrder={{}}
                    disabled={!!summarySearchTerm}
                    searchTerm={summarySearchTerm}
                    onSummaryUpdated={(updatedSummary) => {
                      // The updateSummary action already updates the specific summary in Redux state
                      // No need to reload all summaries
                    }}
                    onSaveToSettings={(newOrder) => {
                      dispatch(saveUserSettings({
                        finalData: {
                          userSettingsDto: {
                            settingName: "SUMMARY_ORDER",
                            settingData: newOrder
                          }
                        },
                        callbackSuccess: (response) => {
                          if (response && response.settingData) {
                            setUserSettingsSummaryOrder(response.settingData);
                          }
                          // Reload summaries to get the exact order from backend
                          dispatch(loadSummaries({
                            callbackSuccess: (summaries) => {
                              // Summaries reloaded successfully
                            },
                            callbackError: (error) => {
                              console.error('Error reloading summaries:', error);
                            }
                          }));
                          notification.success({
                            message: 'Saved Successfully',
                            description: 'Summary order has been saved to your user settings.',
                          });
                        },
                        callbackError: (error) => {
                          console.error('Error saving to user settings:', error);
                          notification.error({
                            message: 'Save Error',
                            description: 'Failed to save summary order: ' + error,
                          });
                        }
                      }));
                    }}
                    onSaveToStack={(newOrder) => {
                      notification.info({
                        message: 'Save to Stack',
                        description: 'Save to stack functionality not available in this context. Use "Save as Permanent" instead.',
                      });
                    }}
                    onGetTitleContent={setSummaryTitleContent}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    {summarySearchTerm ? 'No summaries found matching your search' : 'No summaries available'}
                  </div>
                )}
              </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={12}>
            <Card 
              title={
                <span>
                  <MessageOutlined style={{ marginRight: '8px' }} />
                  Answers ({userAnswers.length})
                </span>
              }
              loading={isAnswersLoading}
            >
              <div style={{ minHeight: '200px' }}>
                {/* Filter by Summary */}
                <div style={{ marginBottom: '16px' }}>
                  <Select
                    placeholder="Filter by summary"
                    allowClear
                    showSearch
                    size={"large"}
                    style={{ width: '100%' }}
                    popupClassName="arabic-font arabic-font-edit"
                    className="arabic-font arabic-font-edit"
                    onChange={(summaryId) => { setSelectedSummaryFilter(summaryId); }}
                    optionFilterProp="children"
                    filterOption={(input, option) => (option?.titleUrdu ?? '').toLowerCase().includes(input.toLowerCase())}
                    fieldNames={{ label: "titleUrdu", value: "id" }}
                    options={userSummaries}
                  />
                </div>
                
                {userAnswers.length > 0 ? (
                  <div>
                    {/* Filter answers based on selected summary */}
                    {(() => {
                      const filteredAnswers = selectedSummaryFilter 
                        ? userAnswers.filter(answer => 
                            answer.summaries && 
                            Array.from(answer.summaries).some(summary => summary.id === selectedSummaryFilter)
                          )
                        : userAnswers;
                      
                      return filteredAnswers.map((answer) => {
                        const isUrduLanguage = answer.answerLanguages && 
                          Array.from(answer.answerLanguages).some(lang => lang === 'urdu' || lang === 'roman_urdu');
                        
                        return (
                          <div
                            key={answer.id}
                            style={{
                              padding: '16px',
                              margin: '8px 0',
                              border: '1px solid #e8e8e8',
                              borderRadius: '8px',
                              backgroundColor: answer.createdBy === currentUserId ? '#ffffff' : '#f5f5f5',
                              opacity: answer.createdBy === currentUserId ? 1 : 0.8,
                              position: 'relative'
                            }}
                          >
                            <TextArea
                              disabled
                              value={answer.answerEnglish || answer.answerUrdu || answer.answerGerman || 'No content available'}
                              className={isUrduLanguage ? "arabic-font" : "roman-font"}
                              style={{
                                color: '#666',
                                fontSize: '14px',
                                marginBottom: '0px',
                                backgroundColor: 'transparent',
                                border: 'none',
                                resize: 'none',
                                overflow: 'hidden',
                                marginTop: answer.createdBy === currentUserId ? '32px' : '0px',
                              }}
                              rows={6}
                              autoSize={{ minRows: 4, maxRows: 8 }}
                            />
                            
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', marginBottom: '0px', color: '#333', fontSize: '12px' }}>
                                  Linked Summaries:
                                </div>
                                {answer.summaries && Array.from(answer.summaries).length > 0 ? (
                                  Array.from(answer.summaries).map(summary => (
                                    <div key={summary.id} style={{
                                      padding: '3px 6px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '3px',
                                      marginBottom: '1px',
                                      border: '1px solid #e9ecef',
                                      fontSize: '8px'
                                    }}
                                    className="arabic-font-tiny"
                                    >
                                      {summary.titleEnglish || summary.titleUrdu || 'Untitled Summary'}
                                    </div>
                                  ))
                                ) : (
                                  <span style={{
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    fontSize: '11px'
                                  }}>
                                    No linked summaries
                                  </span>
                                )}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {answer.createdBy === currentUserId && (
                                  <Button
                                    type="primary"
                                    shape="circle"
                                    size="small"
                                    icon={<EditOutlined />}
                                    style={{
                                      position: 'absolute',
                                      top: '8px',
                                      right: '8px',
                                      width: '28px',
                                      height: '28px',
                                      zIndex: 1
                                    }}
                                    onClick={() => handleEditAnswer(answer)}
                                  />
                                )}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                  <span style={{ fontSize: '12px', color: '#999' }}>
                                    <span style={{ fontWeight: '500', color: '#666' }}>Created: </span>
                                    {answer.created ? moment(answer.created).format('MMM DD, YYYY') : 'Unknown date'}
                                  </span>
                                  {answer.createdBy !== currentUserId && answer.createdByName && (
                                    <span style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                                      <span style={{ fontWeight: '500', color: '#666' }}>By: </span>
                                      {answer.createdByName}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    No answers available
                  </div>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      )}
      
      {/* Edit Summary Dialog */}
      <CreateSummaryDialog 
        showModal={showEditSummaryDialog}
        onHideModal={() => setShowEditSummaryDialog(false)}
        summary={selectedSummary}
        onSummaryUpdated={(updatedSummary) => {
          // The updateSummary action already updates the specific summary in Redux state
          // No need to reload all summaries
          setShowEditSummaryDialog(false);
        }}
      />
      
      {/* Edit Answer Dialog */}
      <CreateAnswerDialog
        key={selectedAnswer?.id}
        showModal={showEditAnswerDialog}
        onHideModal={() => setShowEditAnswerDialog(false)}
        answer={selectedAnswer}
        isUrduLanguage={selectedAnswer?.answerLanguages && Array.from(selectedAnswer.answerLanguages).some(lang => lang === 'urdu' || lang === 'roman_urdu')}
        onAnswerUpdated={(updatedAnswer) => {
          // The updateAnswer action already updates the specific answer in Redux state
          // No need to reload all answers
          setShowEditAnswerDialog(false);
        }}
      />
    </div>
  );
} 